import React from 'react';
import { EvaluationSheetResultItemFieldsFragment } from '../../../../../../../../../../../generated/core-api/graphql-react-query';

export type ModuleGroupedEvaluationSheetResultItems = {
  module: NonNullable<EvaluationSheetResultItemFieldsFragment['complianceModule']> | undefined;
  items: EvaluationSheetResultItemFieldsFragment[];
  totalNum: number;
  totalDenom: number;
};

export default function useModuleGroupedEvaluationSheetResultItems(evaluationSheetResultsItems: EvaluationSheetResultItemFieldsFragment[]) {
  const groupedByModule = React.useMemo<ModuleGroupedEvaluationSheetResultItems[]>(() => {
    const groupedItems = Object.values(
      evaluationSheetResultsItems.reduce<Record<string, EvaluationSheetResultItemFieldsFragment[]>>((acc, item) => {
        const key = item.complianceModule?.id ?? '';
        if (!acc[key]) acc[key] = [];
        acc[key].push(item);
        return acc;
      }, {}),
    );

    return groupedItems
      .map((items) => ({
        module: items[0]?.complianceModule ?? undefined,
        items,
        totalNum: items.reduce((sum, item) => sum + (item.num ?? 0), 0),
        totalDenom: items.reduce((sum, item) => sum + (item.denom ?? 0), 0),
      }))
      .sort((moduleA, moduleB) => {
        if (!moduleB.module) return -1;
        if (!moduleA.module) return 1;
        return moduleA.module.name.localeCompare(moduleB.module.name);
      });
  }, [evaluationSheetResultsItems]);

  const hasAnyModule = React.useMemo(
    () => groupedByModule.some((group) => !!group.module),
    [groupedByModule],
  );

  return {
    groupedByModule,
    hasAnyModule,
  };
}
